import Head from 'next/head'
import Link from 'next/link'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import { Breadcrumb } from '@/commons/Breadcrumb'
import { buildBreadcrumb, breadcrumbJsonLd } from 'utils/breadcrumb'
import { organizationNode, WEBSITE_ID, SITE } from 'utils/local-business-schema'
import { reviewedIso } from 'utils/article-date-time'
import { s3Static } from 'utils/constants'
import { whatsAppDisplay, whatsAppUrl } from 'utils/business-info'
import { airconDemand2026Q3 as data } from 'data/aircon-demand-2026-q3'

/**
 * Aircon servicing demand in Singapore, July-September 2026 - StringsSG's data
 * update on demand through the 2026 haze, from data/aircon-demand-2026-q3.js.
 *
 * Same rule as the annual report: every sentence is computed from the data or
 * states a definition. One more rule here: the spend-adjusted estimate is never
 * presented as a count of requests - it always sits next to the actual requests,
 * labelled as an estimate.
 */

const PUBLISHED = '2026-10-11'
// When this page's content last actually changed. Bump it by hand when you edit the copy.
const CONTENT_REVIEWED = '2026-10-11'

const PATH = '/sg/aircon-servicing-singapore/aircon-servicing-demand-singapore-2026'
const PAGE_URL = `${SITE}${PATH}`
const H1 = 'Aircon Servicing Demand in Singapore: July–September 2026'
const TITLE = 'Aircon Servicing Demand in Singapore, Jul–Sep 2026 | StringsSG Data'
const IMAGE = s3Static + 'blog/living/aircon/chinese-aircon-service-man-smiling.jpg'
const REPORT_PATH = '/sg/aircon-servicing-singapore/singapore-aircon-servicing-report'

const chartPlaceholder = () => <div className='h-full w-full rounded-lg bg-gray-100' />
const loadChart = (name) => dynamic(
    () => import('@/index/service-category/aircon-service/AirconDemandCharts').then((m) => m[name]),
    { ssr: false, loading: chartPlaceholder },
)
const MonthlyDemandChart = loadChart('MonthlyDemandChart')
const SpendAdjustedChart = loadChart('SpendAdjustedChart')
const WeeklyDemandChart = loadChart('WeeklyDemandChart')
const ServiceMixChart = loadChart('ServiceMixChart')
const AreaShareChart = loadChart('AreaShareChart')
const PricePaidChart = loadChart('PricePaidChart')

const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n)}%`
const count = (n) => n.toLocaleString('en-SG')
const perDay = ({ requests, days }) => (requests / days).toFixed(1)
const mean = (values) => (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1)
const listJoin = (items) => items.length > 1 ? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}` : items[0] ?? ''

// Everything the copy states, derived once from the data file.
const summarise = () => {
    const byMonth = Object.fromEntries(data.monthly.map((m) => [m.month, m]))
    const q3Temp = (year) => mean(['07', '08', '09'].map((mm) => byMonth[`${year}-${mm}`].tempC))
    const warmest = data.monthly.reduce((a, b) => (b.tempC > a.tempC ? b : a))
    const [jul, aug, sep] = data.spendAdjusted
    const last = data.periods.length - 1
    const topAreas = [...data.areas].sort((a, b) => b.shares[last] - a.shares[last])
    const general = data.serviceMix.find((s) => s.label === 'General Servicing')
    const wash = data.serviceMix.find((s) => s.label === 'Chemical Wash')
    const repair = data.serviceMix.find((s) => s.label === 'Aircon Repair')
    const priceNow = data.prices[data.prices.length - 1]
    const priceYearAgo = data.prices[0]
    return {
        jul, aug, sep,
        total: data.spendAdjustedTotal,
        q3Temp2025: q3Temp(2025),
        q3Temp2026: q3Temp(2026),
        warmest,
        topFive: topAreas.slice(0, 5).map((a) => a.name),
        topShare: topAreas[0].shares[last],
        general, wash, repair, last,
        priceNow, priceYearAgo,
        dailyBefore: perDay(data.octoberDaily.before),
        dailyHaze: perDay(data.octoberDaily.haze),
    }
}

const buildJsonLd = () => {
    const trail = buildBreadcrumb(PATH, H1)
    return {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'Report',
                '@id': `${PAGE_URL}/#report`,
                headline: H1,
                name: TITLE,
                description: 'Aircon servicing requests received by StringsSG in Singapore through the 2026 haze: monthly and weekly requests, year-on-year change, services requested, busiest areas and prices paid.',
                url: PAGE_URL,
                image: IMAGE,
                inLanguage: 'en-SG',
                datePublished: reviewedIso(PUBLISHED),
                dateModified: reviewedIso(CONTENT_REVIEWED),
                author: { '@id': organizationNode['@id'] },
                publisher: { '@id': organizationNode['@id'] },
                isPartOf: { '@id': WEBSITE_ID },
                mainEntityOfPage: PAGE_URL,
                about: { '@id': `${PAGE_URL}/#data` },
            },
            {
                '@type': 'Dataset',
                '@id': `${PAGE_URL}/#data`,
                name: 'StringsSG aircon servicing requests in Singapore, July 2025 to October 2026',
                description: 'Aircon service requests received by StringsSG in Singapore, by month and week, with services requested, share of requests by area and median prices paid on one-time bookings.',
                url: `${PAGE_URL}#by-month`,
                creator: { '@id': organizationNode['@id'] },
                isAccessibleForFree: true,
                temporalCoverage: `2025-07-01/${data.asOf}`,
                spatialCoverage: { '@type': 'Place', name: 'Singapore' },
                variableMeasured: [
                    'Aircon service requests per month',
                    'Aircon service requests per week',
                    'Share of requests by service (%)',
                    'Share of requests by area (%)',
                    'Median price paid per one-time booking (SGD)',
                ],
                keywords: ['aircon servicing', 'Singapore', 'haze', 'PSI', 'air conditioner maintenance', 'home services data'],
            },
            breadcrumbJsonLd(trail, PAGE_URL),
            organizationNode,
        ],
    }
}

const Section = ({ id, title, children }) => (
    <section id={id} aria-labelledby={`${id}-title`} className="space-y-4 scroll-mt-20">
        <h2 id={`${id}-title`} className="text-xl md:text-2xl font-bold text-gray-900">{title}</h2>
        {children}
    </section>
)

// A chart plus the same figures as a table for screen readers and crawlers.
const Chart = ({ height = 'h-72', caption, columns, rows, children }) => (
    <figure className="space-y-2">
        <div className={`${height} w-full`} aria-hidden="true">{children}</div>
        <figcaption className="text-xs text-gray-500">{caption}</figcaption>
        <table className="sr-only">
            <caption>{caption}</caption>
            <thead><tr>{columns.map((c) => <th key={c.label} scope="col">{c.label}</th>)}</tr></thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={i}>{columns.map((c) => <td key={c.label}>{c.value(r)}</td>)}</tr>
                ))}
            </tbody>
        </table>
    </figure>
)

const link = (href, label) => <Link href={href} prefetch={false} className="text-orange-600 hover:underline">{label}</Link>

export default function AirconServicingDemand2026() {
    const s = summarise()
    const description = `Aircon servicing requests in Singapore through the 2026 haze: requests from 1 to 10 October were ${signed(data.earlyOctober.changePct)} on a year earlier, and the year-on-year gap narrowed every month from July. StringsSG data.`
    const reviewed = new Date(`${CONTENT_REVIEWED}T00:00:00+08:00`).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })

    return (
        <>
            <Head>
                <title>{TITLE}</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={description} />
                <meta name="author" content="StringsSG" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={TITLE} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content="StringsSG" />
                <meta property="og:image" content={IMAGE} />
                <meta property="article:published_time" content={reviewedIso(PUBLISHED)} />
                <meta property="article:modified_time" content={reviewedIso(CONTENT_REVIEWED)} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={TITLE} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={IMAGE} />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={PAGE_URL} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(buildJsonLd()) }} />
            </Head>

            <div className="max-w-4xl mx-auto px-4 pt-4 pb-16">
                <Breadcrumb pageTitle={H1} jsonLd={false} />

                <header className="py-8 space-y-4">
                    <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide">StringsSG data update</p>
                    <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">{H1}</h1>
                    <p className="text-gray-600 leading-7 text-base md:text-lg">
                        How aircon servicing requests to StringsSG moved through the hotter months and the 2026 haze - by
                        month, by week, by service and by area - and what customers paid.
                    </p>
                    <p className="text-xs text-gray-500">
                        Published by StringsSG · Last reviewed {reviewed} · Data to {new Date(`${data.asOf}T00:00:00+08:00`).toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </header>

                <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
                    {[
                        { label: 'Requests, 1–10 Oct 2026, vs the same days of 2025', value: signed(data.earlyOctober.changePct) },
                        { label: `Year-on-year change in September, narrowed from ${signed(s.jul.actualChangePct)} in July`, value: signed(s.sep.actualChangePct) },
                        { label: 'Requests a day in the worst haze days (5–10 Oct), vs 1–4 Oct', value: `${s.dailyHaze} vs ${s.dailyBefore}` },
                        { label: 'Change in StringsSG’s published prices over the past year', value: '0%' },
                    ].map((tile) => (
                        <div key={tile.label} className="rounded-xl border border-orange-200/60 bg-orange-50/40 p-4">
                            <dt className="text-xs text-gray-600">{tile.label}</dt>
                            <dd className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-1">{tile.value}</dd>
                        </div>
                    ))}
                </dl>

                <div className="space-y-14">
                    <Section id="key-findings" title="Key findings">
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-7">
                            <li>
                                Requests from 1 to 10 October 2026 were {data.earlyOctober.changePct}% higher than on the same days
                                of September 2026 and of October 2025. From 5 to 10 October, the worst haze days of the year, we
                                received {s.dailyHaze} requests a day, against {s.dailyBefore} from 1 to 4 October.
                            </li>
                            <li>
                                The year-on-year gap in requests narrowed every month as the haze set in: {signed(s.jul.actualChangePct)} in
                                July, {signed(s.aug.actualChangePct)} in August and {signed(s.sep.actualChangePct)} in September.
                            </li>
                            <li>
                                We spent less on customer acquisition this year. At last year&apos;s spending, this year&apos;s rate of
                                requests per dollar would have produced an estimated {count(s.total.estimate2026)} requests in July to
                                September, {signed(s.total.adjustedChangePct)} on the {count(s.total.requests2025)} received a year
                                earlier. We received {count(s.total.requests2026)}.
                            </li>
                            <li>
                                General servicing was in {s.general.shares[s.last]}% of requests. Chemical wash rose
                                from {s.wash.shares[0]}% a year earlier to {s.wash.shares[s.last]}%, and aircon repair fell
                                from {s.repair.shares[0]}% to {s.repair.shares[s.last]}%.
                            </li>
                            <li>
                                The busiest areas were {listJoin(s.topFive)}. No area made up more than {s.topShare}% of requests.
                            </li>
                            <li>
                                StringsSG&apos;s published prices did not change. The median one-time booking was ${s.priceNow.perBooking},
                                against ${s.priceYearAgo.perBooking} a year earlier, because of what customers booked.
                            </li>
                        </ul>
                    </Section>

                    <Section id="by-month" title="Requests by month">
                        <p className="text-gray-700 leading-7">
                            Aircon service requests received by StringsSG each month, with the mean temperature at Changi. Requests
                            in 2026 peaked in June. December to February, the coolest months, were also the quietest. The warmest
                            month in the period, {s.warmest.label.replace(' ', ' 20')} ({s.warmest.tempC}°C), was not a busy one, so
                            temperature alone does not explain the pattern.
                        </p>
                        <Chart
                            caption="Aircon service requests per month (bars) and NEA mean temperature at Changi (line)"
                            columns={[
                                { label: 'Month', value: (r) => r.label },
                                { label: 'Requests', value: (r) => r.requests },
                                { label: 'Mean temperature (°C)', value: (r) => r.tempC },
                            ]}
                            rows={data.monthly}
                        >
                            <MonthlyDemandChart data={data.monthly} />
                        </Chart>
                    </Section>

                    <Section id="year-on-year" title="Year on year: actual and spend-adjusted">
                        <p className="text-gray-700 leading-7">
                            Requests in July to September 2026 were {signed(s.total.actualChangePct)} on a year earlier, but the gap
                            narrowed every month, from {signed(s.jul.actualChangePct)} in July to {signed(s.sep.actualChangePct)} in
                            September. We also spent less on customer acquisition this year, so the right-hand columns compare like
                            for like: an estimate of the requests this year&apos;s rate per dollar would have produced at last
                            year&apos;s spending. It is an estimate, not a count, and it assumes each extra dollar brings in requests
                            at the same rate.
                        </p>
                        <div className="h-72 w-full" aria-hidden="true">
                            <SpendAdjustedChart data={data.spendAdjusted} />
                        </div>
                        <div className="overflow-x-auto -mx-4 px-4">
                            <table className="w-full min-w-[36rem] text-sm">
                                <caption className="sr-only">Aircon service requests, 2025 and 2026, actual and spend-adjusted</caption>
                                <thead>
                                    <tr className="text-left text-gray-500 border-b border-gray-200">
                                        <th className="py-2 pr-3 font-medium">Month</th>
                                        <th className="py-2 pr-3 font-medium text-right">Requests, 2025</th>
                                        <th className="py-2 pr-3 font-medium text-right">Requests, 2026</th>
                                        <th className="py-2 pr-3 font-medium text-right">Change</th>
                                        <th className="py-2 pr-3 font-medium text-right">2026 estimate at 2025 spending</th>
                                        <th className="py-2 font-medium text-right">Change on that basis</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...data.spendAdjusted, s.total].map((r) => (
                                        <tr key={r.label} className={`border-b border-gray-100 ${r === s.total ? 'font-semibold bg-orange-50/40' : ''}`}>
                                            <td className="py-2 pr-3 text-gray-900">{r.label.replace('-', '–')}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{count(r.requests2025)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{count(r.requests2026)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums">{signed(r.actualChangePct)}</td>
                                            <td className="py-2 pr-3 text-right tabular-nums text-gray-600">{count(r.estimate2026)}</td>
                                            <td className="py-2 text-right tabular-nums text-gray-600">{signed(r.adjustedChangePct)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    <Section id="haze-and-heat" title="Haze and hotter weather">
                        <p className="text-gray-700 leading-7">
                            Weekly requests from June, with the haze period shaded. The PSI first reached the unhealthy range on 4
                            September, reached 167 on 29 September and peaked at 192 on 9 October. The week of 5 October covers six
                            days, to 10 October.
                        </p>
                        <Chart
                            height="h-80"
                            caption="Aircon service requests per week (weeks start Monday), with the 2026 haze period shaded"
                            columns={[
                                { label: 'Week starting', value: (r) => r.week },
                                { label: 'Requests', value: (r) => (r.partial ? `${r.requests} (${r.days} days)` : r.requests) },
                            ]}
                            rows={data.weekly}
                        >
                            <WeeklyDemandChart data={data.weekly} hazeEvents={data.hazeEvents} />
                        </Chart>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-7">
                            <li>
                                <strong>Haze:</strong> in September, requests per dollar of customer acquisition spending
                                rose {signed(data.requestsPerDollarMoM.September)} on August. From 5 to 10 October, as the PSI
                                was unhealthy across the island, requests a day almost doubled, from {s.dailyBefore} to {s.dailyHaze}.
                                Our acquisition spending and approach were unchanged between September and October, so we believe
                                the haze was the most likely factor, although our data alone cannot establish causation.
                            </li>
                            <li>
                                <strong>Hotter weather:</strong> July to September 2026 averaged {s.q3Temp2026}°C at Changi,
                                against {s.q3Temp2025}°C a year earlier.
                            </li>
                            <li>
                                <strong>Areas:</strong> request numbers for individual areas are too small to compare with regional
                                PSI readings. Even the busiest area averages about three requests a week.
                            </li>
                        </ul>
                    </Section>

                    <Section id="services" title="What Singapore asked for">
                        <p className="text-gray-700 leading-7">
                            Share of requests that included each service. A request can include more than one service, so the
                            shares add up to more than 100%. Chemical wash has grown steadily, while aircon repair has fallen.
                        </p>
                        <Chart
                            height="h-[28rem]"
                            caption="Share of aircon service requests including each service, by period"
                            columns={[
                                { label: 'Service', value: (r) => r.label },
                                ...data.periods.map((p, i) => ({ label: `${p} (%)`, value: (r) => r.shares[i] })),
                            ]}
                            rows={data.serviceMix}
                        >
                            <ServiceMixChart rows={data.serviceMix} periods={data.periods} />
                        </Chart>
                    </Section>

                    <Section id="areas" title="Busiest areas">
                        <p className="text-gray-700 leading-7">
                            Share of all requests from each area, for the busiest areas in July to September 2026 and for Sengkang.
                            Demand is spread widely. The busiest areas are among Singapore&apos;s largest housing estates, so their
                            volumes partly reflect how many households they have.{' '}
                            {link(REPORT_PATH, 'Our annual report')} has figures for every area.
                        </p>
                        <Chart
                            height="h-[26rem]"
                            caption="Share of aircon service requests by area, by period"
                            columns={[
                                { label: 'Area', value: (r) => r.name },
                                ...data.periods.map((p, i) => ({ label: `${p} (%)`, value: (r) => r.shares[i] })),
                            ]}
                            rows={data.areas}
                        >
                            <AreaShareChart rows={data.areas} periods={data.periods} />
                        </Chart>
                    </Section>

                    <Section id="prices" title="What customers paid">
                        <p className="text-gray-700 leading-7">
                            StringsSG&apos;s published prices have not changed over the past year. They are fixed up front, include
                            transport and are not adjusted for demand, so the haze and the hotter weather did not raise them. The
                            median amount paid per booking has risen modestly because of what customers booked: the number and
                            type of units and any additional services.{' '}
                            {link('/sg/aircon-servicing-singapore/aircon-servicing-price-singapore', 'See our aircon servicing prices')}.
                        </p>
                        <Chart
                            caption="Median price paid on one-time bookings (SGD, transport included), by period"
                            columns={[
                                { label: 'Period', value: (r) => r.period },
                                { label: 'Median per one-time booking (SGD)', value: (r) => r.perBooking },
                                { label: 'Median per unit, general servicing (SGD)', value: (r) => r.perUnitGeneral },
                            ]}
                            rows={data.prices}
                        >
                            <PricePaidChart data={data.prices} />
                        </Chart>
                    </Section>

                    <Section id="methodology" title="Methodology">
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 leading-7 text-sm md:text-base">
                            <li><strong>Requests</strong> are every aircon service request StringsSG received in Singapore, whether or not it became a booking, dated in Singapore time.</li>
                            <li><strong>The spend-adjusted estimate</strong> is a month&apos;s 2026 requests multiplied by that month&apos;s 2025 customer acquisition spending divided by its 2026 spending. It assumes requests rise in step with spending; because some requests arrive whatever we spend, it is likely to overstate. It is never a count of requests received.</li>
                            <li><strong>Services</strong> are grouped from the items on each request. A request can include several, so shares add up to more than 100%.</li>
                            <li><strong>Areas</strong> are assigned from each request&apos;s service address - by estate or street name where it names one, otherwise by postal sector. Shares are of all requests in the period, and each figure has at least 20 requests behind it.</li>
                            <li><strong>Prices</strong> are medians actually paid on one-time bookings with a technician assigned, transport included.</li>
                            <li>
                                <strong>Temperature</strong> is NEA&apos;s monthly mean surface air temperature at Changi ({link(data.sources.temperature, 'data.gov.sg')}).{' '}
                                <strong>PSI</strong> is the 24-hour PSI as reported from NEA readings ({data.sources.haze.map((h, i) => (
                                    <span key={h.url}>{i > 0 ? ', ' : ''}<a href={h.url} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{h.label}</a></span>
                                ))}; see also <a href={data.sources.nea} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">haze.gov.sg</a>).
                            </li>
                            <li>The week of 5 October covers six days, to 10 October.</li>
                        </ul>
                    </Section>

                    <Section id="cite" title="How to cite this page">
                        <p className="text-gray-700 leading-7">
                            Journalists, researchers and publishers are welcome to cite figures from this page, with a link to it as
                            the source.
                        </p>
                        <blockquote className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800">
                            StringsSG (2026). <em>{H1}</em>. {PAGE_URL}
                        </blockquote>
                        <p className="text-gray-700 leading-7 text-sm">
                            For media enquiries or a data request, message us on WhatsApp at{' '}
                            <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{whatsAppDisplay}</a>.
                        </p>
                    </Section>

                    <Section id="related" title="Related">
                        <ul className="list-disc pl-6 space-y-1.5 text-gray-700">
                            <li>{link(REPORT_PATH, 'Singapore Aircon Servicing Report 2026')}</li>
                            <li>{link('/sg/aircon-servicing-singapore/aircon-servicing-price-singapore', 'Aircon servicing prices in Singapore')}</li>
                            <li>{link('/sg/aircon-servicing-singapore/aircon-servicing-trends-singapore-2026-complete-breakdown', 'Aircon servicing trends in Singapore, 2026')}</li>
                            <li>{link('/sg/aircon-servicing-singapore', 'Book aircon servicing')}</li>
                        </ul>
                    </Section>
                </div>
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
        </>
    )
}
